<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="purple"
    padding="normal"
    initial-height="full"
  >
    <div class="dark:text-slate-200">
      <!-- Header -->
      <div class="mb-6 flex items-start justify-between gap-4">
        <h2 class="flex-1 text-2xl font-bold">{{ $t("pages.photo.add") }}</h2>
        <default-button
          color="blue"
          :disabled="!validated"
          :loading="status.submitting"
          :percentage="percentage"
          @click="submit"
        >
          {{ $t("global.inputs.labels.save") }}
        </default-button>
      </div>

      <!-- Quota Indicator Banner -->
      <div
        v-if="quotaStore.photoQuota"
        class="mb-4 flex items-center justify-between rounded-xl border-2 border-black-dark bg-kekkle-purple/10 px-3.5 py-2.5 text-xs font-bold text-gray-800 dark:bg-kekkle-purple/20 dark:text-gray-200"
      >
        <div class="flex items-center gap-1.5">
          <span class="text-sm">📸</span>
          <span>{{ $t("pages.setting.quota.photo_usage") }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span
            :class="
              quotaStore.isPhotoQuotaExceeded
                ? 'font-black text-kekkle-red'
                : 'text-gray-700 dark:text-gray-300'
            "
          >
            {{ quotaStore.photoQuota.daily_count }} /
            {{ quotaStore.photoQuota.daily_limit }}
            {{ $t("pages.setting.quota.daily").toLowerCase() }}
          </span>
          <span class="text-gray-400">•</span>
          <span class="text-gray-500 dark:text-gray-400">
            {{ quotaStore.photoQuota.total_count }} /
            {{ quotaStore.photoQuota.total_limit }}
            {{ $t("pages.setting.quota.total").toLowerCase() }}
          </span>
        </div>
      </div>

      <!-- Form -->
      <div class="mb-4 w-full">
        <profile-picture-input
          :label="$t('global.inputs.labels.photo') + '*'"
          :large="true"
          :square="true"
          :current-image-url="pictureFileUrl ? pictureFileUrl : ''"
          @change="fileChanged"
        />
        <p v-show="validationErrors.photo" class="mt-1 text-sm text-red-600">
          {{ $t("global.inputs.errors.photo") }}
        </p>
      </div>

      <div class="mb-4 w-full">
        <text-area-input
          v-model="caption"
          :has-error="validationErrors.caption"
          :error-message="$t('global.inputs.errors.photo_caption')"
          :label="$t('global.inputs.labels.photo_description') + '*'"
          :placeholder="$t('global.inputs.placeholders.photo_description')"
        />
      </div>

      <div v-if="pictureFile">
        <p class="dark:text-slate-200">
          {{ $t("pages.photo.date_time_of_picture") }}
          {{ timestamp ? dateTimeAgo(new Date(timestamp)) : "" }}
        </p>
        <p
          class="cursor-pointer underline dark:text-slate-200"
          @click="customDateTime = !customDateTime"
        >
          {{ $t("pages.photo.change_date_time_of_picture") }}
        </p>
      </div>

      <div v-if="customDateTime" class="mb-4 w-full">
        <p class="font-medium dark:text-slate-200">
          {{ $t("global.inputs.labels.date_time") }} *
        </p>
        <p
          v-show="validationErrors.timestamp"
          class="mt-1 text-sm text-red-600"
        >
          {{ $t("global.inputs.errors.date_time") }}
        </p>
        <div class="flex w-full flex-col items-center">
          <date-input v-model="timestamp" :date-time="true" />
        </div>
      </div>
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { usePhotosStore } from "@/stores/modules/photos/photos";
import { useUserStore } from "@/stores/modules/user/user";
import {
  setSuccessToast,
  setWarningToast,
} from "@/helpers/notifications/toast";
import TextAreaInput from "@/components/Inputs/TextAreaInput.vue";
import DateInput from "@/components/Inputs/DateInput.vue";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import ProfilePictureInput from "@/components/ui/ProfilePictureInput.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import { dateObjectToDateString } from "@/helpers/converts/date-converter";
import { dateTimeAgo } from "@/helpers/dates/date-time-ago";
import imageCompression from "browser-image-compression";
import { encodeImageToBlurhash } from "@/helpers/blurhash/encodeImageToBlurhash";
import { firebaseStorage } from "@/helpers/firebase/firebase";
import { ref as storageRef, uploadBytesResumable } from "firebase/storage";
import { generateTrigrams } from "@/helpers/utils/trigrams";
import { useAppReviewPrompt } from "@/composables/useAppReviewPrompt";
import { useQuotaStore } from "@/stores/modules/quota/quota";

const { t } = useI18n();
const groupsStore = useGroupsStore();
const authStore = useAuthStore();
const photosStore = usePhotosStore();
const userStore = useUserStore();
const { maybePromptReview } = useAppReviewPrompt();
const quotaStore = useQuotaStore();

const isOpen = defineModel<boolean>("open", { required: true });

// Form state
const status = ref({
  submitting: false,
});
const caption = ref("");
const timestamp = ref<string | undefined>(undefined);
const pictureFile = ref<File | undefined>(undefined);
const pictureFileUrl = ref<string | undefined>(undefined);
const customDateTime = ref(false);
const percentage = ref(0);

// Computed properties
const groupDocId = computed(() => userStore.activeGroup);

const timestampValid = computed(() => !!timestamp.value);

const captionValid = computed(() => {
  return caption.value.length < 350 && caption.value.length > 2;
});

const validationErrors = computed(() => ({
  photo: !pictureFile.value,
  caption: !captionValid.value,
  timestamp: !timestampValid.value,
}));

const validated = computed(() => {
  return !Object.values(validationErrors.value).some((value) => value === true);
});

// Methods
function resetForm() {
  caption.value = "";
  timestamp.value = dateObjectToDateString(new Date(), "yyyy-mm-ddThh:mm");
  pictureFile.value = undefined;
  pictureFileUrl.value = undefined;
  customDateTime.value = false;
  percentage.value = 0;
}

function fileChanged(file: File) {
  if (file.size / 1020 / 1024 < 500) {
    pictureFile.value = file;
    pictureFileUrl.value = URL.createObjectURL(file);
    if (file.lastModified && file.lastModified > 0) {
      timestamp.value = dateObjectToDateString(
        new Date(file.lastModified),
        "yyyy-mm-ddThh:mm",
      );
    } else {
      timestamp.value = dateObjectToDateString(new Date(), "yyyy-mm-ddThh:mm");
    }
  } else {
    setWarningToast({
      message: t("global.toast.file_too_big", ["500"]),
    });
  }
}

async function submit() {
  status.value.submitting = true;
  if (!validated.value) {
    setWarningToast({
      message: t("global.toast.check_all_fields"),
    });
    status.value.submitting = false;
    return;
  }

  try {
    // Generate a unique ID for the photo
    const fileId = Date.now().toString();
    const photosRef = storageRef(
      firebaseStorage,
      "groups/" + groupDocId.value + "/pictures/" + fileId,
    );

    // Compress images
    const compressedFile = await imageCompression(pictureFile.value!, {
      maxSizeMB: 5,
      maxWidthOrHeight: 3840,
      useWebWorker: true,
    });
    const superCompressedFile = await imageCompression(compressedFile, {
      maxWidthOrHeight: 20,
      useWebWorker: true,
    });
    const blurHash = await encodeImageToBlurhash(
      URL.createObjectURL(superCompressedFile),
    );

    // Upload to Storage
    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(photosRef, compressedFile, {
        customMetadata: { uploadedBy: authStore.authUuid! },
      });
      task.on(
        "state_changed",
        (snapshot) => {
          percentage.value = Math.max(
            Math.min(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
              95,
            ),
            5,
          );
        },
        () => {
          reject();
        },
        () => {
          resolve(true);
        },
      );
    });

    // Save to Firestore via store
    await photosStore.addPhoto({
      absolute_picture_url:
        "groups/" + groupDocId.value + "/pictures/" + fileId,
      picture_url: "pictures/" + fileId,
      caption: caption.value,
      timestamp: new Date(timestamp.value!),
      created_by: authStore.authUuid!,
      trigrams: generateTrigrams(caption.value),
      blurhash: blurHash,
    });

    setSuccessToast({
      message: t("global.toast.photo_saved"),
      emoji: "📸",
    });
    isOpen.value = false;
    maybePromptReview();
    quotaStore.fetchQuota().catch(() => {});
  } catch (e) {
    console.error("Error adding photo:", e);
  } finally {
    status.value.submitting = false;
  }
}

// Watch for opening to reset form
watch(isOpen, (newValue) => {
  if (newValue) {
    resetForm();
  }
});
</script>
